import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Certification data
const certifications = [
  {
    id: 1,
    name: 'FSSAI Licence',
    logo: '/certificates/background.jpg',
    description: 'Our organic spices are certified by the USDA, ensuring they are grown and processed according to federal guidelines addressing soil quality, animal raising practices, pest and weed control, and use of additives.',
    year: '2006',
    pdfPath: '/certificates/fssai-certificate.pdf'
  },
  {
    id: 2,
    name: 'IEC Certificate',
    logo: 'https://images.unsplash.com/photo-1589923188900-85dae523342b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    description: 'Our Fair Trade certification ensures that farmers receive fair prices, workers have safe conditions and fair wages, and strict environmental standards are met during production.',
    year: '2023',
    pdfPath: '/certificates/importer-exporter-code.pdf'
  },
  {
    id: 3,
    name: 'MSME Certificate',
    logo: 'https://images.unsplash.com/photo-1589923188900-85dae523342b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    description: 'Our products are verified by the Non-GMO Project, North America\'s most trusted third-party verification for non-GMO food and products.',
    year: '2023',
    pdfPath: '/certificates/udyam-registration.pdf'
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const Certification: React.FC = () => {
  return (
    <div className="bg-primary-50">
      {/* Hero Section */}
      <section className="relative bg-spice-green text-white py-20">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Certifications</h1>
            <p className="text-xl text-primary-100">
              We're committed to quality, sustainability, and ethical sourcing. Our certifications reflect our dedication to these values.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4">Quality Assurance</h2>
            <p className="text-lg text-primary-600 max-w-2xl mx-auto">
              Our products are certified by leading international organizations, ensuring the highest standards of quality, safety, and sustainability.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {certifications.map((cert) => (
              <motion.div 
                key={cert.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                variants={itemVariants}
              >
                <div className="h-48 bg-primary-100 flex items-center justify-center p-6">
                  <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-md">
                    <span className="text-xl font-bold text-spice-green text-center">{cert.name}</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">{cert.name}</h3>
                    <span className="text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded">Since {cert.year}</span>
                  </div>
                  <p className="text-primary-600 mb-4">{cert.description}</p>
                  <a 
                    href={cert.pdfPath} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center px-4 py-2 bg-spice-red text-white rounded-md hover:bg-spice-red-dark transition-colors duration-300"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-5 w-5 mr-2" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                      />
                    </svg>
                    View Certificate
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quality Testing Process */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Quality Testing Process</h2>
              <p className="text-lg text-primary-600 mb-4">
                Every batch of spices undergoes rigorous testing in our state-of-the-art laboratory to ensure it meets our high standards for quality, purity, and flavor.
              </p>
              <p className="text-lg text-primary-600 mb-4">
                Our testing process includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-primary-600 mb-6">
                <li>Sensory evaluation for aroma, flavor, and appearance</li>
                <li>Moisture content analysis</li>
                <li>Microbial testing for food safety</li>
                <li>Chemical analysis for purity</li>
                <li>Pesticide residue testing</li>
                <li>Essential oil content measurement</li>
              </ul>
              <p className="text-lg text-primary-600">
                Only spices that pass all our quality tests make it into our products, ensuring you receive the finest spices available.
              </p>
            </motion.div>
            
            <motion.div
              className="rounded-lg overflow-hidden shadow-lg"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1581093588401-fbb62a02f120?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                alt="Quality testing lab" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sustainability Commitment */}
      <section className="py-16 bg-spice-yellow bg-opacity-10">
        <div className="container-custom">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4">Our Commitment to Sustainability</h2>
            <p className="text-lg text-primary-600 max-w-3xl mx-auto">
              Beyond certifications, we're committed to sustainable practices throughout our supply chain.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-spice-green rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Sustainable Farming</h3>
              <p className="text-primary-600">
                We work with farmers who use sustainable growing methods that protect soil health, conserve water, and maintain biodiversity.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-spice-green rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Fair Compensation</h3>
              <p className="text-primary-600">
                We ensure farmers receive fair prices for their crops, supporting economic stability in farming communities around the world.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="w-16 h-16 bg-spice-green rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Eco-Friendly Packaging</h3>
              <p className="text-primary-600">
                We're transitioning to sustainable packaging solutions, including recyclable materials and reduced plastic use.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-bold mb-4">Experience Our Quality</h3>
            <p className="text-lg text-primary-600 mb-6 max-w-2xl mx-auto">
              Discover our range of certified, premium spices and taste the difference that quality makes.
            </p>
            <Link to="/products" className="btn btn-primary px-8 py-3">
              Explore Our Products
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Certification; 